// src/components/CamposExtrasTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
    FaSave, FaSpinner, FaBuilding, FaTachometerAlt, FaCalendarAlt,
    FaListAlt, FaDollarSign, FaStickyNote, FaWrench, FaExclamationTriangle,
    FaUserCog, FaShieldAlt, FaChartLine, FaCheckCircle,
} from 'react-icons/fa';
import './CamposExtrasTab.css';

// ─── Estado inicial ──────────────────────────────────────────────────────────
const EMPTY_FORM = {
    unidade_organizacional: '', grupo_unidade_rastreada: '', tipo_manutencao: '',
    nome_etapa: '', situacao: '', prioridade: '',
    data_planejada: '', data_realizacao: '',
    km_planejado: '', km_real: '', horimetro_planejado: '', horimetro_real: '',
    tempo_manutencao: '', hodometro_entrada: '', hodometro_saida: '', nivel_combustivel: '',
    tipo_falha: '', modo_falha: '', codigo_falha: '', impacto_operacional: '',
    itens_previstos: '', itens_nao_previstos: '', itens_cancelados: '',
    itens_migrados: '', itens_realizados: '',
    custo_pecas_planejadas: '', custo_servicos_planejados: '', custo_total_planejado: '',
    custo_pecas_realizadas: '', custo_servicos_realizados: '', custo_total_realizado: '',
    custo_realizacao_pecas_planejadas: '', custo_realizacao_servicos_planejados: '',
    custo_realizacao_pecas_nao_planejadas: '', custo_realizacao_servicos_nao_planejados: '',
    responsavel_tecnico: '', aprovador_financeiro: '', centro_custo: '',
    numero_os_oficina: '', numero_nf_pecas: '', numero_nf_servicos: '',
    garantia_aplicavel: '', vigencia_garantia: '', proxima_revisao_km: '', proxima_revisao_data: '',
    parecer_tecnico: '', observacao_manutencao: '',
};

const toNum = (v) => parseFloat(String(v || '').replace(',', '.')) || 0;
const toStr = (v) => (v === null || v === undefined) ? '' : String(v);

// ─── Section ─────────────────────────────────────────────────────────────────
function Section({ color = '#2563eb', icon, title, children }) {
    return (
        <div className="ce-section" style={{ borderLeftColor: color }}>
            <div className="ce-section-header">
                <span className="ce-section-icon" style={{ color }}>{icon}</span>
                <h4 className="ce-section-title" style={{ color }}>{title}</h4>
            </div>
            <div className="ce-section-body">{children}</div>
        </div>
    );
}

// ─── Field ────────────────────────────────────────────────────────────────────
function Field({ label, name, form, set, type = 'text', placeholder, options, readOnly, hint, span }) {
    const value = toStr(form[name]);
    return (
        <div className="ce-field" style={span ? { gridColumn: `span ${span}` } : {}}>
            <label className="ce-label">{label}</label>
            {options ? (
                <select className="ce-input" value={value} onChange={e => set(name, e.target.value)}>
                    <option value="">— Selecione —</option>
                    {options.map(o =>
                        typeof o === 'string'
                            ? <option key={o} value={o}>{o}</option>
                            : <option key={o.value} value={o.value}>{o.label}</option>
                    )}
                </select>
            ) : type === 'textarea' ? (
                <textarea
                    className="ce-input ce-textarea"
                    rows={3}
                    value={value}
                    placeholder={placeholder}
                    onChange={e => set(name, e.target.value)}
                />
            ) : (
                <input
                    className={`ce-input${readOnly ? ' ce-readonly' : ''}`}
                    type={type}
                    value={value}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    onChange={e => !readOnly && set(name, e.target.value)}
                />
            )}
            {hint && <small className="ce-hint">{hint}</small>}
        </div>
    );
}

// ─── Principal ────────────────────────────────────────────────────────────────
export default function CamposExtrasTab({ ticket }) {
    const [form,    setForm]    = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(true);
    const [saving,  setSaving]  = useState(false);
    const [dirty,   setDirty]   = useState(false);

    const set = useCallback((field, val) => {
        setForm(prev => {
            const next = { ...prev, [field]: val };
            if (field === 'custo_pecas_planejadas' || field === 'custo_servicos_planejados') {
                const t = toNum(field === 'custo_pecas_planejadas' ? val : prev.custo_pecas_planejadas)
                        + toNum(field === 'custo_servicos_planejados' ? val : prev.custo_servicos_planejados);
                next.custo_total_planejado = t > 0 ? t.toFixed(2) : '';
            }
            if (field === 'custo_pecas_realizadas' || field === 'custo_servicos_realizados') {
                const t = toNum(field === 'custo_pecas_realizadas' ? val : prev.custo_pecas_realizadas)
                        + toNum(field === 'custo_servicos_realizados' ? val : prev.custo_servicos_realizados);
                next.custo_total_realizado = t > 0 ? t.toFixed(2) : '';
            }
            return next;
        });
        setDirty(true);
    }, []);

    const fetchData = useCallback(async () => {
        if (!ticket?.id) return;
        setLoading(true);
        try {
            const res = await api.get(`/api/frota/manutencoes/${ticket.id}/campos-extras`);
            if (res.data && typeof res.data === 'object') {
                setForm({ ...EMPTY_FORM, ...res.data });
            }
        } catch (err) {
            if (err.response?.status !== 404) toast.error('Erro ao carregar campos extras.');
        } finally {
            setLoading(false);
            setDirty(false);
        }
    }, [ticket?.id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.post(`/api/frota/manutencoes/${ticket.id}/campos-extras`, form);
            toast.success('Campos extras salvos!');
            setDirty(false);
        } catch {
            toast.error('Erro ao salvar campos extras.');
        } finally {
            setSaving(false);
        }
    };

    const f = { form, set };

    if (loading) return (
        <div className="ce-loading">
            <FaSpinner className="go-spin-icon" />
            <span>Carregando campos extras…</span>
        </div>
    );

    return (
        <div className="ce-root">

            {/* ── Topbar ── */}
            <div className="ce-topbar">
                <div className="ce-topbar-left">
                    {dirty
                        ? <span className="ce-badge warning"><FaExclamationTriangle size={9} /> Alterações não salvas</span>
                        : <span className="ce-badge success"><FaCheckCircle size={9} /> Salvo</span>
                    }
                    <span className="ce-topbar-ref">
                        Manutenção #{ticket?.id} — {ticket?.veiculo_placa} {ticket?.veiculo_modelo}
                    </span>
                </div>
                <button className="ce-save-btn" onClick={handleSave} disabled={saving || !dirty}>
                    {saving ? <><FaSpinner className="go-spin-icon" /> Salvando…</> : <><FaSave /> Salvar Campos</>}
                </button>
            </div>

            {/* ── Seções ── */}
            <div className="ce-scroll">

                <Section color="#2563eb" icon={<FaBuilding />} title="Identificação">
                    <div className="ce-grid g3">
                        <Field {...f} label="Unidade Organizacional"    name="unidade_organizacional"  placeholder="Ex: Filial SP" />
                        <Field {...f} label="Grupo da Unidade Rastreada" name="grupo_unidade_rastreada" placeholder="Ex: Grupo Logística" />
                        <Field {...f} label="Prioridade" name="prioridade" options={[
                            { value: 'urgente', label: '🔴 Urgente' },
                            { value: 'alta',    label: '🟠 Alta'    },
                            { value: 'media',   label: '🟡 Média'   },
                            { value: 'baixa',   label: '🟢 Baixa'   },
                        ]} />
                        <Field {...f} label="Tipo da Manutenção" name="tipo_manutencao" options={[
                            'Preventiva', 'Corretiva', 'Preditiva', 'Detectiva', 'Melhoria',
                        ]} />
                        <Field {...f} label="Nome da Etapa" name="nome_etapa" placeholder="Ex: Diagnóstico inicial" />
                        <Field {...f} label="Situação" name="situacao" options={[
                            'Aguardando peças', 'Em execução', 'Aguardando aprovação',
                            'Paralisada', 'Concluída', 'Cancelada',
                        ]} />
                    </div>
                </Section>

                <Section color="#7c3aed" icon={<FaCalendarAlt />} title="Datas">
                    <div className="ce-grid g2">
                        <Field {...f} label="Data Planejada"    name="data_planejada"  type="date" />
                        <Field {...f} label="Data de Realização" name="data_realizacao" type="date" />
                    </div>
                </Section>

                <Section color="#0891b2" icon={<FaTachometerAlt />} title="Métricas do Veículo">
                    <div className="ce-grid g4">
                        <Field {...f} label="Km Planejado"            name="km_planejado"        type="number" placeholder="0" />
                        <Field {...f} label="Km Real"                  name="km_real"             type="number" placeholder="0" />
                        <Field {...f} label="Horímetro Planejado (h)"  name="horimetro_planejado" type="number" placeholder="0" />
                        <Field {...f} label="Horímetro Real (h)"       name="horimetro_real"      type="number" placeholder="0" />
                        <Field {...f} label="Hodômetro Entrada (km)"   name="hodometro_entrada"   type="number" placeholder="0" />
                        <Field {...f} label="Hodômetro Saída (km)"     name="hodometro_saida"     type="number" placeholder="0" />
                        <Field {...f} label="Nível Combustível (%)"    name="nivel_combustivel"   type="number" placeholder="0–100" />
                        <Field {...f} label="Tempo da Manutenção (h)"  name="tempo_manutencao"    type="number" placeholder="0" />
                    </div>
                </Section>

                <Section color="#dc2626" icon={<FaWrench />} title="Diagnóstico de Falha">
                    <div className="ce-grid g3">
                        <Field {...f} label="Tipo de Falha" name="tipo_falha" options={[
                            'Elétrica', 'Mecânica', 'Hidráulica', 'Pneumática',
                            'Funilaria / Lataria', 'Software / Eletrônica', 'Preventiva (sem falha)',
                        ]} />
                        <Field {...f} label="Modo de Falha" name="modo_falha" options={[
                            'Em operação', 'Veículo parado', 'Preventivo (planejado)', 'Detectado em vistoria',
                        ]} />
                        <Field {...f} label="Impacto Operacional" name="impacto_operacional" options={[
                            'Veículo totalmente parado', 'Redução de capacidade', 'Sem impacto imediato',
                        ]} />
                        <Field {...f} label="Código de Falha (DTC / OBD)" name="codigo_falha"
                            placeholder="Ex: P0300" hint="Código lido pelo scanner" />
                    </div>
                </Section>

                <Section color="#d97706" icon={<FaListAlt />} title="Itens da Manutenção">
                    <div className="ce-grid g5">
                        <Field {...f} label="Previstos"     name="itens_previstos"     type="number" placeholder="0" />
                        <Field {...f} label="Realizados"    name="itens_realizados"    type="number" placeholder="0" />
                        <Field {...f} label="Não Previstos" name="itens_nao_previstos" type="number" placeholder="0" />
                        <Field {...f} label="Cancelados"    name="itens_cancelados"    type="number" placeholder="0" />
                        <Field {...f} label="Migrados"      name="itens_migrados"      type="number" placeholder="0" />
                    </div>
                </Section>

                <Section color="#16a34a" icon={<FaDollarSign />} title="Custos Planejados">
                    <div className="ce-grid g3">
                        <Field {...f} label="Peças Planejadas (R$)"    name="custo_pecas_planejadas"    type="number" placeholder="0,00" />
                        <Field {...f} label="Serviços Planejados (R$)" name="custo_servicos_planejados" type="number" placeholder="0,00" />
                        <Field {...f} label="Total Planejado (R$)"     name="custo_total_planejado"     type="number" readOnly
                            hint="Calculado automaticamente (peças + serviços)" />
                    </div>
                </Section>

                <Section color="#ea580c" icon={<FaChartLine />} title="Custos Realizados">
                    <div className="ce-grid g3">
                        <Field {...f} label="Peças Realizadas (R$)"                name="custo_pecas_realizadas"                    type="number" placeholder="0,00" />
                        <Field {...f} label="Serviços Realizados (R$)"             name="custo_servicos_realizados"                 type="number" placeholder="0,00" />
                        <Field {...f} label="Total Realizado (R$)"                 name="custo_total_realizado"                     type="number" readOnly hint="Calculado automaticamente" />
                        <Field {...f} label="Realiz. Peças Planejadas (R$)"        name="custo_realizacao_pecas_planejadas"         type="number" placeholder="0,00" />
                        <Field {...f} label="Realiz. Serviços Planejados (R$)"     name="custo_realizacao_servicos_planejados"      type="number" placeholder="0,00" />
                        <Field {...f} label="Realiz. Peças NÃO Planejadas (R$)"    name="custo_realizacao_pecas_nao_planejadas"     type="number" placeholder="0,00" />
                        <Field {...f} label="Realiz. Serviços NÃO Planejados (R$)" name="custo_realizacao_servicos_nao_planejados"  type="number" placeholder="0,00" />
                    </div>
                </Section>

                <Section color="#7c3aed" icon={<FaUserCog />} title="Responsáveis & Documentos">
                    <div className="ce-grid g3">
                        <Field {...f} label="Responsável Técnico"    name="responsavel_tecnico"  placeholder="Nome do técnico" />
                        <Field {...f} label="Aprovador Financeiro"   name="aprovador_financeiro" placeholder="Nome do aprovador" />
                        <Field {...f} label="Centro de Custo"        name="centro_custo"         placeholder="Ex: CC-0042" />
                        <Field {...f} label="Nº OS da Oficina (ext.)" name="numero_os_oficina"   placeholder="OS do parceiro" />
                        <Field {...f} label="Nº NF Peças"            name="numero_nf_pecas"      placeholder="Nº da nota fiscal" />
                        <Field {...f} label="Nº NF Serviços"         name="numero_nf_servicos"   placeholder="Nº da nota fiscal" />
                    </div>
                </Section>

                <Section color="#0891b2" icon={<FaShieldAlt />} title="Garantia & Próxima Revisão">
                    <div className="ce-grid g3">
                        <Field {...f} label="Garantia Aplicável" name="garantia_aplicavel" options={[
                            { value: 'sim',     label: '✅ Sim'     },
                            { value: 'nao',     label: '❌ Não'     },
                            { value: 'parcial', label: '⚠️ Parcial' },
                        ]} />
                        <Field {...f} label="Vigência da Garantia"    name="vigencia_garantia"    type="date" />
                        <div />
                        <Field {...f} label="Próxima Revisão — Km"    name="proxima_revisao_km"   type="number" placeholder="0" />
                        <Field {...f} label="Próxima Revisão — Data"  name="proxima_revisao_data" type="date" />
                    </div>
                </Section>

                <Section color="#475569" icon={<FaStickyNote />} title="Parecer & Observações">
                    <div className="ce-grid g1">
                        <Field {...f} label="Parecer Técnico" name="parecer_tecnico" type="textarea"
                            placeholder="Diagnóstico, causa raiz e solução aplicada…" />
                        <Field {...f} label="Observação da Manutenção" name="observacao_manutencao" type="textarea"
                            placeholder="Observações gerais, pendências, alertas…" />
                    </div>
                </Section>

            </div>
        </div>
    );
}